/**
 * Every brand colour in this BMS is the platform's colour (or the one the admin hub set for this
 * BMS), or the business's own once it chooses them, and shades of it: nothing else is built in.
 * Until any is known (the hub hasn't answered yet), NEUTRAL: a grey, not a brand.
 */
export const NEUTRAL = '#6B7280';

/**
 * Module accent colours from the branding config. The wholesale module uses its own
 * colours, or the retail ones when its branding is set to "use the retail colours"
 * (enable_custom_wholesale_colors = false) - the same rule BrandingContext applies to the
 * CSS variables.
 */

export const retailAccent = (config) =>
  config?.retail_accent_color || config?.app_accent_color || config?.accent_color || NEUTRAL;

export const wholesaleAccent = (config) =>
  config?.enable_custom_wholesale_colors === false
    ? retailAccent(config)
    : config?.wholesale_accent_color ||
      config?.wholesale_secondary_color ||
      config?.app_accent_color ||
      config?.accent_color ||
      NEUTRAL;

/**
 * Sidebar colours.
 *
 * Each module's sidebar background either follows each user's theme, or is a set colour
 * blended with the user's theme: strength 100 is the set colour, 0 the theme's own. The
 * wholesale module uses the retail setting when it is set to the retail colours. Text,
 * border and hover colours are derived from the resulting background so the sidebar is
 * readable in every theme; the accent (active item, logo tile) is the brand's.
 */

// Each theme's surface colour (--color-bg-secondary in index.css)
export const THEME_SURFACES = {
  light: '#FFFFFF',
  charcoal: '#2C2C2C',
  midnight: '#1E293B',
  ocean: '#162033'
};

const THEME_SIDEBAR = {
  bg: 'var(--color-bg-secondary)',
  text: 'var(--color-text-primary)',
  border: 'var(--color-border)',
  hover: 'var(--color-bg-tertiary)'
};

const LIGHT_TEXT = '#F9FAFB';
const DARK_TEXT = '#1F2937';

const toRgb = (hex) => {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!match) return null;
  const digits =
    match[1].length === 3
      ? match[1]
          .split('')
          .map((c) => c + c)
          .join('')
      : match[1];
  return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16));
};

const toHex = (rgb) =>
  `#${rgb
    .map((v) => Math.round(v).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

/** `a` and `b` mixed: weight 1 is all `a`, 0 all `b` */
export const mixColours = (a, b, weight) => {
  const x = toRgb(a);
  const y = toRgb(b);
  if (!x || !y) return a;
  return toHex(x.map((v, i) => v * weight + y[i] * (1 - weight)));
};

const luminance = (hex) => {
  const [r, g, b] = toRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (hi + 0.05) / (lo + 0.05);
};

/** The sidebar background setting a module uses: { mode, color, strength } */
export const sidebarBackground = (config, module) => {
  const own = module === 'wholesale' && config?.enable_custom_wholesale_colors !== false;
  const prefix = own ? 'wholesale' : 'retail';
  const strength = Number(config?.[`${prefix}_sidebar_bg_strength`]);
  return {
    mode: config?.[`${prefix}_sidebar_bg_mode`] === 'color' ? 'color' : 'theme',
    color: config?.[`${prefix}_sidebar_bg_color`] || brandPalette(config)[own ? 'wholesalePrimary' : 'primary'],
    strength: Number.isFinite(strength) ? Math.min(100, Math.max(0, strength)) : 100
  };
};

/** The sidebar's colours for a module in a theme: { bg, text, border, hover } */
export const sidebarColours = (config, module, themeId) => {
  const { mode, color, strength } = sidebarBackground(config, module);
  if (mode !== 'color' || !toRgb(color)) return THEME_SIDEBAR;
  const bg = mixColours(color, THEME_SURFACES[themeId] || THEME_SURFACES.light, strength / 100);
  const text = contrast(LIGHT_TEXT, bg) >= contrast(DARK_TEXT, bg) ? LIGHT_TEXT : DARK_TEXT;
  return {
    bg,
    text,
    border: mixColours(text, bg, 0.14),
    hover: mixColours(text, bg, 0.08)
  };
};

/** A deep shade of a colour: for primaries (headers, sidebars, document titles). */
export const deepShade = (hex) => mixColours(hex, '#000000', 0.35);

/**
 * The colours in effect: the platform's (the colour the admin hub set for this BMS, or the
 * platform's own), unless the business chose its own (use_platform_colors = false): the app's
 * accents and primaries, document colours and sidebars are all that colour and its shades. While
 * the platform's colour isn't known yet, NEUTRAL, never a colour built into the app.
 */
export const withPlatformColours = (config, platformColours) => {
  if (!config || config.use_platform_colors === false) return config;
  const brand = platformColours?.primaryColor || NEUTRAL;
  const accent = platformColours?.accentColor || brand;
  const deep = deepShade(brand);
  return {
    ...config,
    enable_custom_app_colors: true,
    app_accent_color: brand,
    app_accent_light: mixColours(brand, '#FFFFFF', 0.7),
    app_accent_dark: mixColours(brand, '#000000', 0.8),
    app_primary_color: deep,
    primary_color: deep,
    accent_color: brand,
    retail_accent_color: brand,
    retail_primary_color: deep,
    retail_sidebar_bg_color: deep,
    enable_custom_wholesale_colors: true,
    wholesale_accent_color: brand,
    wholesale_secondary_color: accent,
    wholesale_primary_color: deep,
    wholesale_sidebar_bg_color: deep
  };
};

/**
 * The app's palette from the colours in effect (after withPlatformColours): every CSS colour
 * variable comes from here, so each is the brand colour or a shade of it.
 */
export function brandPalette(config) {
  const accent = retailAccent(config);
  const primary = config?.retail_primary_color || config?.app_primary_color || config?.primary_color || deepShade(accent);
  const retail = {
    accent,
    accentLight: config?.app_accent_light || mixColours(accent, '#FFFFFF', 0.7),
    accentDark: config?.app_accent_dark || mixColours(accent, '#000000', 0.8),
    primary,
    border: config?.retail_border_color || primary,
    hover: config?.retail_hover_color || primary,
    text: config?.retail_text_color || mixColours(accent, '#FFFFFF', 0.12),
    // Chart series: the brand colour stepped darker and lighter, distinct enough to tell apart
    series: [
      accent,
      mixColours(accent, '#000000', 0.65),
      mixColours(accent, '#FFFFFF', 0.55),
      mixColours(accent, '#000000', 0.4),
      mixColours(accent, '#FFFFFF', 0.3),
      mixColours(accent, '#000000', 0.85)
    ]
  };
  if (config?.enable_custom_wholesale_colors === false) {
    return {
      ...retail,
      wholesaleAccent: retail.accent,
      wholesalePrimary: retail.primary,
      wholesaleSecondary: retail.border,
      wholesaleText: retail.text,
      wholesaleLight: mixColours(retail.accent, '#FFFFFF', 0.15)
    };
  }
  const wholesaleAccent = wholesaleAccentOf(config);
  const wholesalePrimary = config?.wholesale_primary_color || deepShade(wholesaleAccent);
  return {
    ...retail,
    wholesaleAccent,
    wholesalePrimary,
    wholesaleSecondary: config?.wholesale_secondary_color || config?.wholesale_border_color || wholesaleAccent,
    wholesaleText: config?.wholesale_text_color || mixColours(wholesaleAccent, '#FFFFFF', 0.12),
    wholesaleLight: mixColours(wholesaleAccent, '#FFFFFF', 0.15)
  };
}

// The wholesale accent without the retail fallback rule (brandPalette applies it)
const wholesaleAccentOf = (config) =>
  config?.wholesale_accent_color || config?.wholesale_secondary_color || retailAccent(config);

/** A colour's 50…950 scale (as Tailwind's palettes): lighter below 500, deeper above. */
export const scaleOf = (hex) => ({
  50: mixColours(hex, '#FFFFFF', 0.08),
  100: mixColours(hex, '#FFFFFF', 0.16),
  200: mixColours(hex, '#FFFFFF', 0.32),
  300: mixColours(hex, '#FFFFFF', 0.5),
  400: mixColours(hex, '#FFFFFF', 0.72),
  500: hex,
  600: mixColours(hex, '#000000', 0.85),
  700: mixColours(hex, '#000000', 0.7),
  800: mixColours(hex, '#000000', 0.55),
  900: mixColours(hex, '#000000', 0.42),
  950: mixColours(hex, '#000000', 0.28)
});

/** "R G B" for a colour: CSS variables Tailwind can apply transparency to. */
export const rgbChannels = (hex) => (toRgb(hex) || toRgb(NEUTRAL)).join(' ');

/**
 * A CSS colour variable's current value (for PDFs, canvases and anything that can't read CSS
 * variables itself), e.g. cssColour('--color-accent').
 */
export const cssColour = (name) => {
  try {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || NEUTRAL;
  } catch {
    return NEUTRAL;
  }
};
